import { useQueryClient } from "@tanstack/react-query";
import { FormikProps, useFormik } from "formik";
import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useCreateGroupNote } from "@/queries/group-visit/use-create-group-note";
import {
  GROUP_NOTE_QUERY_KEY,
  useGroupNoteBySessionId,
} from "@/queries/group-visit/use-group-note-by-session-id";
import { useUpdateGroupNote } from "@/queries/group-visit/use-update-group-note";
import { getErrorSnackbar, getSuccessSnackbar } from "@/utils/utils";
import { SESSION_NOTE_INITIAL_VALUES } from "../group-visit-constant";
import { SessionNoteValues } from "../group-visit-types";
import { toGroupNotePayload, toSessionNoteValues } from "../group-visit-utils";

export type UseSharedGroupNote = {
  formik: FormikProps<SessionNoteValues>;
  isLoading: boolean;
  isSaving: boolean;
  /** The note has been saved at least once in this session. */
  isComplete: boolean;
  save: () => Promise<void>;
};

/**
 * The one note that covers every attendee, read from and written to the
 * session's shared note. It is created on the first save and updated after.
 *
 * @author Rajkumar Rathod
 */
export const useSharedGroupNote = (sessionId?: string): UseSharedGroupNote => {
  const client = useQueryClient();
  const showSnackbar = useSnackbar();
  const [isComplete, setComplete] = useState(false);
  const [loadedFor, setLoadedFor] = useState<string | undefined>();

  const { data: note, isLoading } = useGroupNoteBySessionId(sessionId);
  const { mutateAsync: createNote, isPending: isCreating } =
    useCreateGroupNote();
  const { mutateAsync: updateNote, isPending: isUpdating } =
    useUpdateGroupNote();

  const formik = useFormik<SessionNoteValues>({
    initialValues: SESSION_NOTE_INITIAL_VALUES,
    onSubmit: () => undefined,
  });
  const { values, resetForm } = formik;

  // Load the saved note once per session, so a refetch never overwrites typing.
  useEffect(() => {
    if (!sessionId || isLoading) return;
    if (loadedFor === sessionId) return;
    resetForm({ values: toSessionNoteValues(note) });
    setLoadedFor(sessionId);
  }, [sessionId, isLoading, note, loadedFor, resetForm]);

  const save = useCallback(async () => {
    if (!sessionId) return;
    const params = {
      sessionId,
      groupVisitSharedNote: toGroupNotePayload(values),
    };

    try {
      const res = await (note ? updateNote(params) : createNote(params));
      showSnackbar(getSuccessSnackbar(res));
      setComplete(true);
      await client.invalidateQueries({
        queryKey: [GROUP_NOTE_QUERY_KEY, sessionId],
      });
    } catch (err) {
      showSnackbar(getErrorSnackbar(err));
    }
  }, [sessionId, values, note, updateNote, createNote, showSnackbar, client]);

  return {
    formik,
    isLoading: !!sessionId && isLoading,
    isSaving: isCreating || isUpdating,
    // A saved note was marked complete, so it stays complete after a reload.
    isComplete: isComplete || !!note,
    save,
  };
};
